import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Tooltip, CircularProgress,
  Button, Chip,
} from '@mui/material';
import ArrowBackIcon        from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon     from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon  from '@mui/icons-material/ArrowBackIosNew';
import FullscreenIcon       from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon   from '@mui/icons-material/FullscreenExit';
import TvIcon               from '@mui/icons-material/Tv';
import StopIcon             from '@mui/icons-material/Stop';
import TimerIcon            from '@mui/icons-material/Timer';
import { getPitch, updatePitch, loadBackgrounds } from './services/pitchService';
import SlideRenderer        from './slides/SlideRenderer';
import { useAltLogo }       from '../../hooks/useQRLogo';
import { fontFamily, colors } from '../../theme/tikabTokens';

// ── Timer hook ─────────────────────────────────────────────────────────────────
function useTimer() {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const interval = useRef(null);

  const start = useCallback(() => {
    if (running) return;
    setRunning(true);
    interval.current = setInterval(() => setSeconds(s => s + 1), 1000);
  }, [running]);

  const reset = useCallback(() => {
    clearInterval(interval.current);
    setRunning(false);
    setSeconds(0);
  }, []);

  useEffect(() => () => clearInterval(interval.current), []);

  const fmt = s => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return { time: fmt(seconds), running, start, reset };
}

export default function PresenterView() {
  const { pitchId } = useParams();
  const navigate    = useNavigate();

  const [pitch,       setPitch]       = useState(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [current,     setCurrent]     = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hdmiWindow,  setHdmiWindow]  = useState(null);

  const containerRef = useRef(null);
  const altLogoUrl   = useAltLogo();
  const timer = useTimer();

  // ── Load pitch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const [p, bgs] = await Promise.all([
        getPitch(pitchId),
        loadBackgrounds(),
      ]);
      if (p) {
        setPitch(p);
        setCurrent(p.currentSlide ?? 0);
      }
      setBackgrounds(bgs);
      setLoading(false);
    })();
  }, [pitchId]);

  // ── Slide navigation ────────────────────────────────────────────────────────
  const totalSlides = pitch?.slides?.length ?? 0;

  const goTo = useCallback(async (idx) => {
    if (!pitch) return;
    const clamped = Math.max(0, Math.min(idx, totalSlides - 1));
    setCurrent(clamped);
    // Sync to Firestore so AudienceView follows
    await updatePitch(pitchId, { currentSlide: clamped });
  }, [pitch, pitchId, totalSlides]);

  const prev = useCallback(() => goTo(current - 1), [goTo, current]);
  const next = useCallback(() => goTo(current + 1), [goTo, current]);

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') next();
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')  prev();
      if (e.key === 'Escape' && isFullscreen) exitFullscreen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, isFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fullscreen ──────────────────────────────────────────────────────────────
  function enterFullscreen() {
    containerRef.current?.requestFullscreen?.();
  }
  function exitFullscreen() {
    document.exitFullscreen?.();
  }
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── HDMI window ─────────────────────────────────────────────────────────────
  function openHdmi() {
    const w = window.open(
      `${window.location.origin}/pitch/${pitchId}/audience`,
      'hdmi-screen',
      'width=1280,height=720',
    );
    setHdmiWindow(w);
    // Push current slide immediately
    updatePitch(pitchId, { currentSlide: current });
  }
  function closeHdmi() {
    hdmiWindow?.close();
    setHdmiWindow(null);
  }

  // ── Derived ─────────────────────────────────────────────────────────────────
  const slides      = (pitch?.slides ?? []).filter(s => !s.hidden);
  const currentSlide = slides[current]  ?? null;
  const nextSlide    = slides[current + 1] ?? null;
  const notes        = currentSlide?.speakerNotes ?? '';
  const nextNotes    = nextSlide?.speakerNotes ?? '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#111' }}>
        <CircularProgress sx={{ color: colors.orange }} />
      </Box>
    );
  }

  if (!pitch) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#111' }}>
        <Typography color="error">Pitch hittades inte</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        bgcolor: '#0d0d0d',
        display: 'flex',
        flexDirection: 'column',
        fontFamily,
        color: 'white',
        userSelect: 'none',
      }}
    >
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      {!isFullscreen && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1,
          bgcolor: '#1a1a1a',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          <Tooltip title="Tillbaka till redigering">
            <IconButton size="small" sx={{ color: '#aaa' }}
              onClick={() => navigate(`/pitch/${pitchId}/edit`)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#eee', mr: 'auto' }}>
            {pitch.title || 'Namnlös pitch'}
            {pitch.client && (
              <Typography component="span" variant="caption" sx={{ color: '#aaa', ml: 1 }}>
                — {pitch.client}
              </Typography>
            )}
          </Typography>

          {/* Timer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimerIcon sx={{ fontSize: 16, color: '#aaa' }} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: timer.running ? '#4caf50' : '#aaa', minWidth: 44 }}>
              {timer.time}
            </Typography>
            {!timer.running
              ? <Button size="small" onClick={timer.start} sx={{ color: '#aaa', fontSize: '0.7rem', py: 0.25 }}>Start</Button>
              : <Button size="small" onClick={timer.reset} sx={{ color: '#aaa', fontSize: '0.7rem', py: 0.25 }}>Reset</Button>
            }
          </Box>

          <Chip
            label={`${current + 1} / ${totalSlides}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#ccc', fontFamily }}
          />

          {/* HDMI button */}
          {!hdmiWindow ? (
            <Tooltip title="Skjut ut till HDMI-skärm">
              <Button
                size="small"
                startIcon={<TvIcon />}
                onClick={openHdmi}
                sx={{
                  color: '#ccc', borderColor: 'rgba(255,255,255,0.2)',
                  border: '1px solid', borderRadius: 1, px: 1.5, py: 0.5,
                  fontSize: '0.75rem', textTransform: 'none',
                  '&:hover': { borderColor: colors.orange, color: colors.orange },
                }}
              >
                HDMI
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Stäng HDMI-fönster">
              <Button
                size="small"
                startIcon={<StopIcon />}
                onClick={closeHdmi}
                sx={{
                  color: '#f44', borderColor: 'rgba(244,68,68,0.4)',
                  border: '1px solid', borderRadius: 1, px: 1.5, py: 0.5,
                  fontSize: '0.75rem', textTransform: 'none',
                }}
              >
                Stäng HDMI
              </Button>
            </Tooltip>
          )}

          {/* Fullscreen button */}
          <Tooltip title="Helskärm">
            <IconButton size="small" sx={{ color: '#aaa', '&:hover': { color: colors.orange } }}
              onClick={isFullscreen ? exitFullscreen : enterFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* ── Main area ───────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Current slide (large preview) */}
        <Box sx={{
          flex: isFullscreen ? 1 : '0 0 65%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: isFullscreen ? 'center' : 'flex-start',
          p: isFullscreen ? 0 : 3,
          gap: 2,
          overflow: 'hidden',
        }}>
          {currentSlide ? (
            <Box sx={{
              width: '100%',
              maxWidth: isFullscreen ? '100%' : 900,
              borderRadius: isFullscreen ? 0 : 2,
              overflow: 'hidden',
              boxShadow: isFullscreen ? 'none' : '0 8px 48px rgba(0,0,0,0.8)',
            }}>
              <SlideRenderer interactive slide={currentSlide} backgrounds={backgrounds} slideTheme={pitch?.slideTheme} altLogoUrl={altLogoUrl}
                qrUrl={pitch?.slideTheme?.showQr && pitch?.shareToken ? `${window.location.origin}/p/${pitch.shareToken}` : undefined}
                qrLogoUrl={pitch?.qrLogoUrl || undefined} />
            </Box>
          ) : (
            <Box sx={{ width: '100%', maxWidth: 900, aspectRatio: '16/9',
              bgcolor: '#222', borderRadius: 2, display: 'flex',
              alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: '#555', fontFamily }}>Inga slides</Typography>
            </Box>
          )}

          {/* Talarnoter — aktuell slide, stor text */}
          {!isFullscreen && (
            <Box sx={{ width: '100%', maxWidth: 900, px: 1 }}>
              <Typography variant="caption" sx={{ color: '#555', mb: 1, display: 'block', fontFamily, letterSpacing: '0.08em' }}>
                TALARNOTER
              </Typography>
              {notes ? (
                <Typography sx={{ color: '#ddd', fontSize: '1.45rem', lineHeight: 1.6, fontFamily, whiteSpace: 'pre-wrap' }}>
                  {notes}
                </Typography>
              ) : (
                <Typography sx={{ color: '#3a3a3a', fontSize: '1.1rem', fontStyle: 'italic', fontFamily }}>
                  Inga talarnoter för denna slide
                </Typography>
              )}
            </Box>
          )}

          {/* Navigation controls */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            ...(isFullscreen && {
              position: 'absolute', bottom: 24, left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.6)',
              px: 2, py: 1, borderRadius: 4,
              backdropFilter: 'blur(12px)',
            }),
          }}>
            <IconButton
              onClick={prev}
              disabled={current === 0}
              sx={{ color: current === 0 ? '#333' : '#aaa', '&:hover': { color: 'white' } }}
            >
              <ArrowBackIcon />
            </IconButton>

            {/* Slide dots */}
            <Box sx={{ display: 'flex', gap: 0.5, maxWidth: 300, overflow: 'hidden' }}>
              {slides.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => goTo(i)}
                  sx={{
                    width: i === current ? 20 : 6, height: 6,
                    borderRadius: 3,
                    bgcolor: i === current ? colors.orange : 'rgba(255,255,255,0.25)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexShrink: 0,
                    '&:hover': { bgcolor: i === current ? colors.orange : 'rgba(255,255,255,0.5)' },
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={next}
              disabled={current >= totalSlides - 1}
              sx={{ color: current >= totalSlides - 1 ? '#333' : '#aaa', '&:hover': { color: 'white' } }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Right panel — only in non-fullscreen mode */}
        {!isFullscreen && (
          <Box sx={{
            flex: '0 0 35%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}>
            {/* Next slide preview */}
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography variant="caption" sx={{ color: '#555', mb: 1, display: 'block', fontFamily }}>
                NÄSTA SLIDE
              </Typography>
              {nextSlide ? (
                <Box sx={{
                  borderRadius: 1.5, overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
                  opacity: 0.7,
                }}>
                  <SlideRenderer slide={nextSlide} backgrounds={backgrounds} slideTheme={pitch?.slideTheme} altLogoUrl={altLogoUrl}
                    qrUrl={pitch?.slideTheme?.showQr && pitch?.shareToken ? `${window.location.origin}/p/${pitch.shareToken}` : undefined}
                    qrLogoUrl={pitch?.qrLogoUrl || undefined} />
                </Box>
              ) : (
                <Box sx={{
                  aspectRatio: '16/9', bgcolor: '#1a1a1a', borderRadius: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ color: '#333', fontSize: '0.75rem', fontFamily }}>
                    {current >= totalSlides - 1 ? 'Sista sliden' : 'Inga fler slides'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Talarnoter nästa slide */}
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
              <Typography variant="caption" sx={{ color: '#555', mb: 1.5, display: 'block', fontFamily, letterSpacing: '0.08em' }}>
                TALARNOTER (NÄSTA)
              </Typography>
              {nextNotes ? (
                <Typography sx={{ color: '#888', fontSize: '0.95rem', lineHeight: 1.65, fontFamily, whiteSpace: 'pre-wrap' }}>
                  {nextNotes}
                </Typography>
              ) : (
                <Typography sx={{ color: '#333', fontSize: '0.85rem', fontStyle: 'italic', fontFamily }}>
                  {nextSlide ? 'Inga talarnoter' : 'Sista sliden'}
                </Typography>
              )}
            </Box>

            {/* Slide type badge */}
            {currentSlide && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Chip
                  label={currentSlide.type}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#666', fontFamily, fontSize: '0.7rem' }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
